
import React from 'react';
import { motion } from 'framer-motion';
import HeroContent from './HeroContent';
import { useIsMobile } from '@/hooks/use-mobile';

const HeroContainer = () => {
  const isMobile = useIsMobile();
  
  return (
    <motion.section 
      className="relative overflow-hidden bg-gradient-to-br from-[#001233] to-[#023047] text-white min-h-[75vh] pt-16 pb-2" // Added pt-16 to account for navbar height
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      id="hero"
    >
      {/* Grid pattern overlay with subtle fade in */}
      <motion.div 
        className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0wIDBoNDB2NDBoLTQweiIvPjxwYXRoIGQ9Ik0wIDBoMXY0MGgtMXpNMzkgMGgxdjQwaC0xek0wIDBoNDB2MWgtNDB6TTAgMzloNDB2MWgtNDB6IiBmaWxsPSJyZ2JhKDI1NSwgMjU1LCAyNTUsIDAuMSkiLz48L2c+PC9zdmc+')] opacity-30"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.3 }}
        transition={{ duration: 0.6 }}
      ></motion.div>
      
      {/* Tech-style glow effects with subtle animation */}
      <motion.div 
        className="absolute top-20 right-10 w-72 h-72 rounded-full bg-[#33C3F0]/20 blur-3xl"
        animate={{ 
          scale: [1, 1.05, 1],
          opacity: [0.15, 0.2, 0.15]
        }}
        transition={{ 
          duration: 8, 
          repeat: Infinity, 
          ease: "easeInOut" 
        }}
      ></motion.div>
      
      <motion.div 
        className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-[#FF6B6B]/20 blur-3xl"
        animate={{ 
          scale: [1, 1.03, 1],
          opacity: [0.15, 0.22, 0.15]
        }}
        transition={{ 
          duration: 10, 
          repeat: Infinity, 
          ease: "easeInOut",
          delay: 2
        }}
      ></motion.div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 pt-2 pb-2 md:pt-4 items-center min-h-[60vh]">
          {/* Left side - Hero content */}
          <div className="md:col-span-7 lg:col-span-6">
            <HeroContent />
          </div>
          
          {/* Right side - Tech visual element */}
          <div className="md:col-span-5 lg:col-span-6 hidden md:flex justify-center items-center">
            <motion.div 
              className="relative w-full h-full"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative w-64 h-64">
                  {/* Rotating rings with smoother animation */}
                  <motion.div 
                    className="absolute inset-0 border-2 border-dashed border-[#33C3F0] rounded-full"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
                  ></motion.div>
                  <motion.div 
                    className="absolute inset-4 border-2 border-dashed border-[#FF6B6B] rounded-full"
                    animate={{ rotate: -360 }}
                    transition={{ duration: 35, repeat: Infinity, ease: "linear" }}
                  ></motion.div>
                  <motion.div 
                    className="absolute inset-8 border-2 border-dashed border-[#29BF12] rounded-full"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                  ></motion.div>
                  
                  {/* Center glowing dot - subtle pulse */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <motion.div 
                      className="w-10 h-10 rounded-full bg-gradient-to-r from-[#FF6B6B] to-[#33C3F0]"
                      animate={{ scale: [1, 1.05, 1] }}
                      transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                    >
                      <div className="absolute inset-0 bg-white rounded-full blur-md opacity-50"></div>
                    </motion.div>
                  </div>
                  
                  {/* Floating tech elements - smoother float animation */}
                  <motion.div 
                    className="absolute top-4 left-16 w-10 h-10 flex items-center justify-center"
                    animate={{ y: [0, -6, 0] }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <div className="w-8 h-8 bg-gray-800 rounded-md flex items-center justify-center shadow-lg">
                      <div className="text-[#FF6B6B] text-xl font-bold">&lt;/&gt;</div>
                    </div>
                  </motion.div>
                  
                  <motion.div 
                    className="absolute top-16 right-6 w-10 h-10 flex items-center justify-center"
                    animate={{ y: [0, 8, 0] }}
                    transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                  >
                    <div className="w-10 h-10 bg-gray-800 rounded-md flex items-center justify-center shadow-lg">
                      <div className="text-[#29BF12] text-xl font-bold">{ "{}" }</div>
                    </div>
                  </motion.div>
                  
                  <motion.div 
                    className="absolute bottom-12 left-12 w-10 h-10 flex items-center justify-center"
                    animate={{ y: [0, 6, 0] }}
                    transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                  >
                    <div className="w-10 h-10 bg-gray-800 rounded-md flex items-center justify-center shadow-lg">
                      <div className="text-[#33C3F0] text-xl font-bold">#</div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.section>
  );
};

export default HeroContainer;
